import { useState, useEffect, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { Button } from '../../../../shared/components/Button.js';
import { Badge } from '../../../../shared/components/Badge.js';
import { Alert } from '../../../../shared/components/Alert.js';
import { EmptyState } from '../../../../shared/components/EmptyState.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/** An active hold (GET /v1/payments/hold/active), with its bill and vendor. */
interface Hold {
  holdId: string;
  bill: BillRef | null;
  vendorName: string | null;
  vendorCode: string | null;
  holdAmount: number | null;
  holdReasonCode: string;
  holdReasonDetail: string | null;
  holdPlacedAt: string;
  heldByName: string | null;
  heldByCode: string | null;
}

const cell = { padding: '12px 14px', borderBottom: '1px solid var(--color-border-default)', verticalAlign: 'middle' } as const;
const head = { ...cell, fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-muted)', textAlign: 'left', background: 'var(--color-bg-muted)' } as const;

/**
 * L15.08 — Payment Holds: the payments held from Payment Entry, each with why,
 * and their release. A released payment returns to Payment Entry at the stage
 * it was held at.
 */
export function HoldsPage(): ReactNode {
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay, day } = useBusinessDay();
  const { token } = useAuth();
  const [holds, setHolds] = useState<Hold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [releaseReason, setReleaseReason] = useState('');

  useEffect(() => {
    apiRequest<Hold[]>('/v1/payments/hold/active', { token })
      .then(setHolds)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleRelease = async (holdId: string) => {
    if (!releaseReason.trim()) {
      setError(t('finance.holds.reasonRequired'));
      return;
    }
    try {
      await apiRequest<unknown>(`/v1/payments/hold/${holdId}/release`, { token, method: 'PATCH', body: { releaseReason: releaseReason.trim() } });
      setHolds(prev => prev.filter(h => h.holdId !== holdId));
      setReleasingId(null);
      setReleaseReason('');
      setError(null);
      setNotice(t('finance.holds.released'));
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="tl-page" style={{ padding: 24 }}>
      <PageHeader title={t('finance.holds.title')} subtitle={t('finance.holds.subtitle')} />
      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      {loading && <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.holds.loading')}</p>}
      {!loading && holds.length === 0 && <EmptyState title={t('finance.holds.empty')} />}
      {holds.length > 0 && (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={head}>{t('finance.holds.colClaim')}</th>
                  <th style={head}>{t('finance.holds.colVendor')}</th>
                  <th style={{ ...head, textAlign: 'right' }}>{t('finance.holds.colAmount')}</th>
                  <th style={head}>{t('finance.holds.colReason')}</th>
                  <th style={head}>{t('finance.holds.colHoldDate')}</th>
                  <th style={head}>{t('finance.holds.colAction')}</th>
                </tr>
              </thead>
              <tbody>
                {holds.map((h) => (
                  <tr key={h.holdId} data-testid={`hold-row-${h.holdId}`}>
                    <td style={cell}>{billRefLabel(h.bill, day)}</td>
                    <td style={cell}>{personLabel(h.vendorName, h.vendorCode) ?? '—'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{h.holdAmount === null ? '—' : money(h.holdAmount)}</td>
                    <td style={cell}>
                      <Badge tone="warning">{t(`finance.holdReason.${h.holdReasonCode}`)}</Badge>
                      {h.holdReasonDetail && <div style={{ marginTop: 6, color: 'var(--color-text-muted)' }}>{h.holdReasonDetail}</div>}
                    </td>
                    <td style={cell}>
                      {atDay(h.holdPlacedAt)}
                      <div style={{ color: 'var(--color-text-muted)' }}>{personLabel(h.heldByName, h.heldByCode) ?? '—'}</div>
                    </td>
                    <td style={cell}>
                      {releasingId === h.holdId ? (
                        <div className="tl-stack tl-stack--row" style={{ gap: 8, alignItems: 'center' }}>
                          <input
                            data-testid={`release-reason-${h.holdId}`}
                            value={releaseReason}
                            onChange={e => setReleaseReason(e.target.value)}
                            placeholder={t('finance.holds.reasonPlaceholder')}
                            style={{ height: 36, padding: '0 10px', borderRadius: 8, border: '1px solid var(--color-border-default)' }}
                          />
                          <Button size="sm" variant="primary" onClick={() => void handleRelease(h.holdId)}>{t('finance.holds.confirm')}</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setReleasingId(null); setReleaseReason(''); setError(null); }}>{t('finance.holds.cancel')}</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="secondary" onClick={() => { setReleasingId(h.holdId); setReleaseReason(''); setNotice(null); }}>{t('finance.holds.release')}</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
